// ABOUTME: One Escape belongs to one overlay — the one on top of everything else open — and what lies under it is not
// ABOUTME: being looked at. Shared by the modal primitives, so a dialog over a window does not close both at once

import { createContext, useCallback, useContext, useEffect, useId } from "react"

interface OpenOverlay {
  id: string
  zIndex: number
  /** Order of opening, which settles ties between overlays sharing a z-index. */
  seq: number
}

const open: OpenOverlay[] = []
let opened = 0
const listeners = new Set<() => void>()
let isTelling = false

/**
 * The listeners hear of a change once the pile has settled. An overlay built anew in one commit (a
 * component made inside a hook, `useLocalConfirmation`'s) leaves the pile in its cleanup before the
 * new one joins in its effect: told in between, what lies beneath would think itself uncovered.
 */
const changed = () => {
  if (isTelling) return
  isTelling = true
  queueMicrotask(() => {
    isTelling = false
    listeners.forEach((listener) => listener())
  })
}

/**
 * An overlay still waiting for its z-index will get the highest yet (getNextZIndex only counts up).
 * useZIndex hands it out in an effect, and an update made in an effect renders at default priority
 * (react-dom-client.development.js:18360-18374, React 19.2.5): after the commit that joined the
 * overlay to the pile, and after the listeners were told.
 */
const height = (entry: OpenOverlay) => (entry.zIndex === 0 ? Number.POSITIVE_INFINITY : entry.zIndex)

const topmost = (): OpenOverlay | undefined =>
  open.reduce<OpenOverlay | undefined>(
    (best, entry) =>
      !best || height(entry) > height(best) || (height(entry) === height(best) && entry.seq > best.seq) ? entry : best,
    undefined,
  )

/**
 * Registers an overlay while it is open and answers whether it is the one on top.
 *
 * Every primitive listens for Escape on `document`, so without a shared order one keypress ran two
 * handlers: a confirmation opened over a window closed the confirmation *and* the window under it,
 * carrying off the work the confirmation was asking about. The stack is shared on purpose —
 * `CModalCore` and `CModalDialog` are different components but the same pile of overlays on screen.
 */
export const useEscapeOwnership = (isOpen: boolean, zIndex: number): (() => boolean) => {
  const id = useId()

  useEffect(() => {
    if (!isOpen) return
    const entry: OpenOverlay = { id, zIndex, seq: ++opened }
    open.push(entry)
    changed()
    return () => {
      const at = open.indexOf(entry)
      if (at !== -1) open.splice(at, 1)
      changed()
    }
  }, [isOpen, id, zIndex])

  return useCallback(() => topmost()?.id === id, [id])
}

/** Calls back whenever an overlay opens or closes, so what lies under one can tell when it is uncovered. */
export function subscribeOverlays(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Outside any overlay is the page, on top while nothing is open over it. */
const OnTopContext = createContext<() => boolean>(() => open.length === 0)

/** Given by the modal primitives to what they show: whether their overlay is the one on top. */
export const OnTopProvider = OnTopContext.Provider

/**
 * Whether what this component shows is on top, asked at the moment it matters: an
 * IntersectionObserver tells what is on screen, not what another overlay covers.
 */
export const useIsOnTop = (): (() => boolean) => useContext(OnTopContext)
